import { IcAlert20, IcEye20, IcHeight20, IcPdv20 } from '@assets/icons/20x20';
import { Grid, HStack, Icon, Text } from '@chakra-ui/react';
import { appIntl } from '@components/locale/IntlGlobalProvider';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectHeightUnit } from '@redux/settings/settings.selectors';
import {
  getClearanceStatusIcon,
  getNotificationColor,
  getWorstNotificationLevel,
  isDroneNotification,
  isPerimeterNotification,
} from '@utils/data/notification.utils';
import { getReliabilityStatusIcon } from '@utils/target.utils';
import { convertDistanceUnitFromMeter, formatUnit } from '@utils/unit.utils';
import { isEqual } from 'lodash';
import { memo } from 'react';
import { FormattedMessage } from 'react-intl';

import { TargetStatusEnum } from '@/types/c2/c2.types';
import { AlertLevelEnum, DroneNotificationLight, NotificationType } from '@/types/data/data.types';
import { DataInfoSizeEnum } from '@/types/target.types';

type Props = {
  notification: NotificationType;
  isArchived: boolean;
  isSearch: boolean;
};

function DroneCardInfo(notification: DroneNotificationLight, isArchived: boolean, level: AlertLevelEnum | null) {
  const showCurrent = !isArchived && notification.endTime == null;
  const showLast = showCurrent && notification.targetStatus === TargetStatusEnum.LOST;
  const prefixLabel = showLast ? 'last' : 'max';
  const prefix = !showCurrent || showLast ? appIntl().formatMessage({ id: `global.${prefixLabel}` }) : undefined;
  const dataInfoIconSize = DataInfoSizeEnum.NOTIFICATION_CARD;
  const heightUnit = useAppSelector(selectHeightUnit);

  return (
    <>
      <HStack gap={1} wrap="nowrap">
        <Icon as={getClearanceStatusIcon(notification.clearance, dataInfoIconSize)} />
        <Text>
          <FormattedMessage id={`shortClearance.${notification.clearance ?? 'UNKNOWN'}`} />
        </Text>
      </HStack>
      <HStack gap={1}>
        <Icon as={IcAlert20} boxSize="20px" color={getNotificationColor(notification, { isArchived })} />
        <Text>
          <FormattedMessage id={`notification.criticality.${level}`} />
        </Text>
      </HStack>
      <HStack gap={1}>
        <Icon
          as={getReliabilityStatusIcon(notification.reliability, dataInfoIconSize)}
          boxSize="20px"
          color="neutral.300"
        />
        <Text>
          <FormattedMessage id={`target.reliability.${notification.reliability}`} />
        </Text>
      </HStack>
      <HStack gap={1}>
        <Icon as={IcEye20} boxSize="20px" color="neutral.300" />
        <Text>{`x${notification.occurrenceCount}`}</Text>
      </HStack>
      <HStack gap={1}>
        <Icon as={IcPdv20} boxSize="20px" color="neutral.300" />
        <Text>
          <FormattedMessage
            id="target.fp"
            values={{ value: showCurrent ? notification.hasFlightPlan : notification.hasHadActiveFlightPlan }}
          />
        </Text>
      </HStack>
      <HStack gap={1}>
        <Icon as={IcHeight20} boxSize="20px" color="neutral.300" />
        <Text>
          {formatUnit(
            heightUnit,
            convertDistanceUnitFromMeter(
              heightUnit,
              showCurrent
                ? notification.currentHeight
                : (notification.maxTransmittedAgl ?? notification.maxComputedAgl),
            ),
            {
              prefix,
            },
          )}
        </Text>
      </HStack>
    </>
  );
}

function NotificationCardInfo({ notification, isArchived, isSearch }: Readonly<Props>) {
  const level = getWorstNotificationLevel(notification, { isArchived });

  function getWidth() {
    if (isSearch) {
      return '296px';
    } else if (isArchived) {
      return '344px';
    }
    return '360px';
  }

  return (
    <Grid
      width={getWidth()}
      height="100%"
      paddingX={2}
      paddingY={3}
      templateColumns={`repeat(2, 1fr)`}
      templateRows={`repeat(3, 1fr)`}
      gap={1}
      flexShrink={0}
      rowGap="auto"
      columnGap={2.5}
    >
      {isDroneNotification(notification) ? (
        DroneCardInfo(notification, isArchived, level)
      ) : (
        <>
          <HStack gap={1}>
            <Icon as={IcAlert20} boxSize="20px" color={getNotificationColor(notification, { isArchived })} />
            <Text>
              <FormattedMessage id={`notification.criticality.${level}`} />
            </Text>
          </HStack>
          {isPerimeterNotification(notification) && (
            <HStack gap={1}>
              <Text>
                <FormattedMessage id={`automatonSection.segment.${notification.segmentType}`} />
              </Text>
            </HStack>
          )}
        </>
      )}
    </Grid>
  );
}

export default memo(NotificationCardInfo, isEqual);
